// Cart Item Component
// Displays a single item in the shopping cart

import type { CartItem as CartItemType } from '@/types';
import styles from './CartItem.module.css';

interface CartItemProps {
  item: CartItemType;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onRemove: (itemId: string) => void;
}

export function CartItem({ item, onUpdateQuantity, onRemove }: CartItemProps) {
  const product = item.product;

  // Format price
  const formatPrice = (price: number) => {
    return `$${Math.round(price).toLocaleString()}`;
  };

  // Get display image
  const displayImage = product.featuredImageUrl || product.imageUrls?.[0] || '';

  // Build variant display text
  const variantText = item.variantAttributes
    ? Object.entries(item.variantAttributes)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ')
    : null;

  return (
    <div className={styles.cartItem}>
      {/* Product Image */}
      <div className={styles.imageContainer}>
        {displayImage ? (
          <img src={displayImage} alt={product.title} className={styles.image} />
        ) : (
          <div className={styles.imagePlaceholder}>No Image</div>
        )}
      </div>

      {/* Product Info */}
      <div className={styles.productInfo}>
        <h3 className={styles.productTitle}>{product.title}</h3>
        {variantText && <p className={styles.variantText}>{variantText}</p>}
        <p className={styles.price}>{formatPrice(item.subtotal)}</p>
      </div>

      {/* Quantity Controls */}
      <div className={styles.quantityControls}>
        <button
          className={styles.quantityButton}
          onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
          disabled={item.quantity <= 1}
          aria-label="Decrease quantity"
        >
          −
        </button>
        <span className={styles.quantity}>{item.quantity}</span>
        <button
          className={styles.quantityButton}
          onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
          aria-label="Increase quantity"
        >
          +
        </button>
      </div>

      {/* Remove Button */}
      <button
        className={styles.removeButton}
        onClick={() => onRemove(item.id)}
        aria-label="Remove item"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 6h18" />
          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
        </svg>
      </button>
    </div>
  );
}
